import { useEffect } from 'react'
import { StoreApi, UseBoundStore } from 'zustand'
import { CurrencyCode, TAppStore } from '../appStore'
import { localCache } from '@hotcoin/utils/dist/utils/cache'
import locales from 'core/config/locale.json'
import { useGetCurrencyListQuery } from 'core/query/yapi/hk-web/Hotcoin/query'
import { useLocalStorageListener } from './useLocalStorageListener'
import { TTimezone } from 'core/types'

export interface Currency {
  id: number
  englishName: string
  symbol: string
  currencyCode: string
  chineseName: string
  precision: number
  status: number
  exchangeRate: number
  sort: number
}

export interface Settings {
  currencyItem: Currency
  currencyList: Currency[]
  currencyValue: string
  isSetCurrency: boolean
  style: number
}

// todo: @itachi 帮忙验证下该兼容逻辑是否正确
// todo: 兼容vue老版本上的临时方案，后续重构完之后需要删除
const useFixLegacy = (appStore: React.MutableRefObject<UseBoundStore<StoreApi<TAppStore>> | undefined>) => {
  const { data: currencyData } = useGetCurrencyListQuery()

  /* 重构页面的状态修改需要同步到老页面 */
  useEffect(() => {
    const currentState = appStore?.current?.getState()

    if (currentState) {
      const currentCurrency = currencyData?.data?.data.find(
        (item) => item.currencyCode === currentState.appSetting.currencyCode
      )
      if (currentCurrency) {
        localCache.setCache('settings', {
          currencyItem: currentCurrency,
          currencyList: currencyData?.data?.data,
          currencyValue: currentCurrency?.symbol,
          isSetCurrency: true,
          style: currentState.appSetting.theme === 'dark' ? 1 : 2,
        })
      }

      localCache.setCache(
        'curLocale',
        locales.find((item) => item.value === currentState.appSetting.locale)
      )
    }

    return appStore?.current?.subscribe((state, prevState) => {
      // 兼容逻辑。
      /* 重构页面主题同步到老页面 */
      if (state.appSetting.theme !== prevState.appSetting.theme) {
        localCache.setCache('settings', {
          ...localCache.getCache('settings'),
          style: state.appSetting.theme === 'dark' ? 1 : 2,
        })
      }
      /* 重构页面涨跌色同步到老页面 */
      if (state.appSetting.trendColor !== prevState.appSetting.trendColor) {
        localStorage.setItem('colors', state.appSetting.trendColor === 'reverse' ? 'true' : 'false')
      }
      /* 重构页面多语言同步到老页面 */
      if (state.appSetting.locale !== prevState.appSetting.locale) {
        localCache.setCache(
          'curLocale',
          locales.find((item) => item.value === state.appSetting.locale)
        )
      }
      /* 重构页面折算货币同步到老页面 */
      if (state.appSetting.currencyCode !== prevState.appSetting.currencyCode) {
        if (currencyData?.data?.data) {
          const currentCurrency = currencyData?.data?.data.find(
            (item) => item.currencyCode === state.appSetting.currencyCode
          )
          localCache.setCache('settings', {
            ...localCache.getCache('settings'),
            currencyItem: currentCurrency,
            currencyList: currencyData?.data?.data,
            currencyValue: currentCurrency?.symbol,
            isSetCurrency: true,
          })
        }
      }
      /* 重构页面涨跌幅基准同步到老页面 */
      if (state.appSetting.granularity !== prevState.appSetting.granularity) {
        if (typeof state.appSetting.granularity === 'undefined') {
          localStorage.removeItem('rangeBenchmark')
        } else {
          localStorage.setItem('rangeBenchmark', state.appSetting.granularity)
        }
      }
    })
  }, [appStore, currencyData])

  /* 老页面的状态修改需要同步到重构页面 */
  useLocalStorageListener<Settings>('settings', (newValue, oldValue) => {
    if (newValue?.style !== oldValue?.style) {
      // 设置theme到cookie中
      document.cookie = `theme=${newValue?.style === 1 ? 'dark' : 'light'}; path=/; max-age=31536000`
      appStore?.current?.setState((state) => ({
        ...state,
        appSetting: {
          ...state.appSetting,
          theme: newValue?.style === 1 ? 'dark' : 'light',
        },
      }))
    }
    if (
      newValue?.currencyItem?.currencyCode !== oldValue?.currencyItem?.currencyCode &&
      newValue?.currencyItem?.currencyCode
    ) {
      appStore?.current?.setState((state) => ({
        appSetting: {
          ...state.appSetting,
          currencyCode: newValue.currencyItem.currencyCode as CurrencyCode,
          currencySymbol: newValue.currencyItem.symbol,
        },
      }))
    }
  })
  /*  老页面涨跌色同步到重构页面 */
  useLocalStorageListener<boolean>('colors', (newValue, oldValue) => {
    if (newValue !== oldValue) {
      // 设置trendColor到cookie中
      document.cookie = `trendColor=${newValue ? 'reverse' : 'normal'}; path=/; max-age=31536000`
      appStore?.current?.setState((state) => ({
        ...state,
        appSetting: {
          ...state.appSetting,
          trendColor: newValue ? 'reverse' : 'normal',
        },
      }))
    }
  })
  /* 老页面涨跌幅基准同步到重构页面 */
  useLocalStorageListener<TTimezone>('rangeBenchmark', (newValue, oldValue) => {
    if (newValue !== oldValue) {
      appStore?.current?.setState((state) => ({
        appSetting: {
          ...state.appSetting,
          granularity: newValue,
        },
      }))
    }
  })
}

export default useFixLegacy
